import React from 'react'
import {
  Show,
  DateField,
  TextField,
  EditButton,
  Button,
  FunctionField,
  TopToolbar,
  useRecordContext,
  Labeled,
  TabbedShowLayout,
  ArrayField,
  Datagrid,
} from 'react-admin';
import { Chip, Box, Link } from '@mui/material';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { getEcgStatus } from './ecgStatus';
import ResendActivationEmailButton from './ResendActivationEmailButton';

const StatusChips = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {record.activated
        ? <Chip label="Activated" color="success" size="small" />
        : <Chip label="Not activated" color="error" size="small" />}
      {record.pending_invitation && <Chip label="Pending invitation" color="warning" size="small" />}
      {record.banned && <Chip label="Banned" color="error" size="small" />}
    </Box>
  );
};

const LoginAsButton = () => {
  const record = useRecordContext();
  if (!record?.login_as_url) return null;
  return (
    <Button
      label="Login as user"
      onClick={() => window.open(record.login_as_url, '_blank', 'noopener,noreferrer')}
      startIcon={<SwitchAccountIcon />}
    />
  );
};

// Equimetre billing links, populated automatically by the API for admins
const EquimetreBillingLinks = () => {
  const record = useRecordContext();
  if (!record) return null;

  const infos = (record.equimetre_billing_infos || []).filter(info => info?.stripe_id);
  if (infos.length === 0) {
    return <span style={{ color: '#999' }}>No Equimetre billing info found</span>;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {infos.map((info, i) => (
        <Link
          key={i}
          href={`https://quote.equimetre.com/billing/${info.stripe_id}`}
          target="_blank"
          rel="noopener noreferrer"
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          <CreditCardIcon fontSize="small" />
          {info.billing_email} — Stripe {info.stripe_id}
        </Link>
      ))}
    </Box>
  );
};

const EcgStatusDetails = () => {
  const record = useRecordContext();
  if (!record) return null;

  const status = getEcgStatus(record);
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
      <Chip
        label={status.label}
        size="small"
        sx={{ bgcolor: status.color, color: '#fff' }}
      />
      {status.dates.map((line, i) => (
        <span key={i} style={{ color: '#666', fontSize: '0.85rem' }}>{line}</span>
      ))}
    </Box>
  );
};

const SubscriptionStatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;
  return record.paid_date
    ? <Chip label="Paid" color="success" size="small" />
    : <Chip label="Payment pending" color="warning" size="small" />;
};

const SubscriptionPaymentLink = () => {
  const record = useRecordContext();
  if (!record) return null;
  if (!record.stripe_payment_link) return <span style={{ color: '#999' }}>—</span>;
  return (
    <Link
      href={record.stripe_payment_link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      Payment link
    </Link>
  );
};

const LastLoginOrNever = () => {
  const record = useRecordContext();
  if (!record) return null;
  if (!record.last_login_date) return <span style={{ color: '#999' }}>Jamais connecté</span>;
  return <DateField source="last_login_date" showTime />;
};

const ShowTopButtons = () => {
  return (
    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
      <LoginAsButton />
      <ResendActivationEmailButton />
    </Box>
  );
};

const UserShowActions = () => {
  return (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  );
};

const UserShow = (props) => {
  return (
    <Show {...props} title="User Details" actions={<UserShowActions />}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Details">
          <ShowTopButtons />
          <TextField source="id" label="ID" />
          <TextField source="firstname" />
          <TextField source="lastname" />
          <TextField source="email" />
          <TextField source="lang" label="Language" />
          <FunctionField label="Status" render={() => <StatusChips />} />
          <Labeled label="ECG access">
            <EcgStatusDetails />
          </Labeled>
          <FunctionField label="Last login" render={() => <LastLoginOrNever />} />
          <Labeled label="Equimetre billing">
            <EquimetreBillingLinks />
          </Labeled>
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Subscriptions" path="subscriptions">
          <ArrayField source="subscriptions">
            <Datagrid bulkActionButtons={false} rowClick={false}>
              <FunctionField label="Status" render={() => <SubscriptionStatusChip />} />
              <DateField source="creation_date" label="Created" showTime />
              <DateField source="paid_date" label="Paid at" showTime emptyText="—" />
              <TextField source="periodicity" />
              <FunctionField
                label="Price"
                render={(record) => record.price != null ? `${record.price} ${record.currency || ''}`.trim() : '—'}
              />
              <TextField source="email" />
              <TextField source="company" emptyText="—" />
              <FunctionField label="Payment link" render={() => <SubscriptionPaymentLink />} />
            </Datagrid>
          </ArrayField>
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default UserShow
